import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
});

// Add request interceptor to include auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Shared by concurrent 401s so a burst of parallel requests (e.g. a
// dashboard firing several calls at once) triggers exactly one refresh
// instead of each one racing the backend's single-use rotating refresh
// token (whichever refresh call loses the race wipes out the tokens the
// winner just saved).
let refreshingPromise = null;

function doRefresh() {
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) {
    return Promise.reject(new Error('No refresh token'));
  }

  return axios
    .post(`${API_BASE_URL}/auth/refresh-token`, { refreshToken })
    .then((response) => {
      const { accessToken, refreshToken: newRefreshToken } = response.data.data;
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', newRefreshToken);
      return accessToken;
    });
}

// Add response interceptor to handle token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        if (!refreshingPromise) {
          refreshingPromise = doRefresh().finally(() => {
            refreshingPromise = null;
          });
        }
        const accessToken = await refreshingPromise;

        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;

// API Methods
export const authAPI = {
  login: (email, password) => {
    console.log('📡 API Call: POST /auth/login with email:', email);
    return apiClient.post('/auth/login', { email, password, portal: 'admin' })
      .then(res => {
        console.log('📡 API Response: Login successful');
        return res;
      })
      .catch(err => {
        console.error('📡 API Error: Login failed', err.message);
        throw err;
      });
  },
  register: (email, password, fullName) =>
    apiClient.post('/auth/register', { email, password, fullName }),
  me: () => apiClient.get('/auth/me'),
  logout: () => apiClient.post('/auth/logout'),
};

export const userAPI = {
  list: (params) => apiClient.get('/users', { params }),
  create: (data) => apiClient.post('/users', data),
  get: (id) => apiClient.get(`/users/${id}`),
  update: (id, data) => apiClient.put(`/users/${id}`, data),
  delete: (id) => apiClient.delete(`/users/${id}`),
  assignScreens: (id, screenIds) => apiClient.post(`/users/${id}/screens`, { screenIds }),
  getAssignedScreens: (id) => apiClient.get(`/users/${id}/screens`),
};

export const screenAPI = {
  list: (params) => apiClient.get('/screens', { params }),
  create: (data) => apiClient.post('/screens', data),
  get: (id) => apiClient.get(`/screens/${id}`),
  update: (id, data) => apiClient.put(`/screens/${id}`, data),
  delete: (id) => apiClient.delete(`/screens/${id}`),
  stats: () => apiClient.get('/screens/stats'),
  generateToken: (id) => apiClient.get(`/screens/${id}/token`),
  testPlay: (id, videoId) => apiClient.post(`/screens/${id}/test-play`, { videoId }),
  testStop: (id) => apiClient.post(`/screens/${id}/test-stop`),
};

export const videoAPI = {
  list: (params) => apiClient.get('/videos', { params }),
  upload: (formData) =>
    apiClient.post('/videos', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  get: (id) => apiClient.get(`/videos/${id}`),
  approve: (id) => apiClient.post(`/videos/${id}/approve`),
  reject: (id, reason) => apiClient.post(`/videos/${id}/reject`, { reason }),
  delete: (id) => apiClient.delete(`/videos/${id}`),
  stats: () => apiClient.get('/videos/stats'),
};

export const playlistAPI = {
  list: (params) => apiClient.get('/playlists', { params }),
  create: (data) => apiClient.post('/playlists', data),
  get: (id) => apiClient.get(`/playlists/${id}`),
  update: (id, data) => apiClient.put(`/playlists/${id}`, data),
  delete: (id) => apiClient.delete(`/playlists/${id}`),
  addVideo: (playlistId, videoId, playOrder) =>
    apiClient.post(`/playlists/${playlistId}/items`, {
      videoId,
      playOrder,
    }),
  removeVideo: (playlistId, videoId) =>
    apiClient.delete(`/playlists/${playlistId}/items/${videoId}`),
  reorder: (id, items) =>
    apiClient.post(`/playlists/${id}/reorder`, { items }),
};

export const scheduleAPI = {
  list: (params) => apiClient.get('/schedules', { params }),
  create: (data) => apiClient.post('/schedules', data),
  get: (id) => apiClient.get(`/schedules/${id}`),
  update: (id, data) => apiClient.put(`/schedules/${id}`, data),
  delete: (id) => apiClient.delete(`/schedules/${id}`),
  byScreen: (screenId, params) =>
    apiClient.get(`/schedules/screen/${screenId}`, { params }),
};

export const reportAPI = {
  dashboard: () => apiClient.get('/reports/dashboard'),
  playback: (params) => apiClient.get('/reports/playback', { params }),
  playbackTimeseries: (params) => apiClient.get('/reports/playback/timeseries', { params }),
  screen: (screenCode, params) =>
    apiClient.get(`/reports/screen/${screenCode}`, { params }),
  revenue: (params) => apiClient.get('/reports/revenue', { params }),
  videoSchedule: (params) => apiClient.get('/reports/video-schedule', { params }),
  recentActivity: (params) => apiClient.get('/reports/recent-activity', { params }),
};
